import React, { useState, useRef, useEffect } from "react";
import { useFetchProducts } from "./hooks/useFetchProducts";
import { useFetchBrands } from "./hooks/useFetchBrands";
import {
  Box,
  CircularProgress,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  Chip
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridActionsCellItem
} from "@mui/x-data-grid";
import { useSearchParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDeleteProduct } from "./hooks/useProductActions";

const CustomToolbar = ({ onCreateNew }) => {
  return (
    <GridToolbarContainer sx={{ justifyContent: "space-between" }}>
      <Button
        variant="contained"
        color="primary"
        onClick={onCreateNew}
        sx={{ mb: 1 }}
      >
        Create New Product
      </Button>
      <GridToolbarExport />
    </GridToolbarContainer>
  );
};

export const ProductsList = ({ onRowClick, onCreateNew }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(
    parseInt(searchParams.get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize")) || 20
  );
  const [sortModel, setSortModel] = useState([
    {
      field: searchParams.get("sortField") || "createdAt",
      sort: searchParams.get("sortOrder") || "desc"
    }
  ]);
  const [brandId, setBrandId] = useState(searchParams.get("brandId") || "");
  const [name, setName] = useState(searchParams.get("name") || "");
  const [searchInput, setSearchInput] = useState(name);

  const deleteProductMutation = useDeleteProduct();

  const searchTimeout = useRef(null);

  const {
    data: productsData,
    isLoading,
    error
  } = useFetchProducts({
    page,
    pageSize,
    brandId: brandId || undefined,
    name: name || undefined,
    sortField: sortModel[0]?.field || "createdAt",
    sortOrder: sortModel[0]?.sort || "desc"
  });

  const { data: brandsData } = useFetchBrands();

  const getBrandName = (brand) =>
    brand.Vendor?.vendorName || brand.vendorName || brand.brandName || brand.name || "";

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) { params.set("page", page); }
    if (pageSize !== 20) { params.set("pageSize", pageSize); }
    if (sortModel[0]?.field !== "createdAt") {
      params.set("sortField", sortModel[0]?.field);
    }
    if (sortModel[0]?.sort !== "desc") {
      params.set("sortOrder", sortModel[0]?.sort);
    }
    if (brandId) { params.set("brandId", brandId); }
    if (name) { params.set("name", name); }

    setSearchParams(params);
  }, [page, pageSize, sortModel, brandId, name, setSearchParams]);

  const handleSearchChange = (newValue) => {
    setSearchInput(newValue);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setName(newValue);
      setPage(1);
    }, 500);
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProductMutation.mutateAsync(productId);
      } catch (error) {
        console.error("Failed to delete product:", error);
      }
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
      filterable: false,
      sortable: true
    },
    {
      field: "name",
      headerName: "Name",
      width: 250,
      filterable: false,
      sortable: true
    },
    {
      field: "title",
      headerName: "Title",
      width: 200,
      filterable: false,
      sortable: true
    },
    {
      field: "brandName",
      headerName: "Brand",
      width: 150,
      filterable: false,
      sortable: true
    },
    {
      field: "description",
      headerName: "Description",
      width: 150,
      filterable: false,
      sortable: true
    },
    {
      field: "price",
      headerName: "Price",
      width: 100,
      filterable: false,
      sortable: true,
      renderCell: (params) => {
        return params.value ? `$${parseFloat(params.value).toFixed(2)}` : "-";
      }
    },
    {
      field: "canonicalUrl",
      headerName: "URL",
      width: 200,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) { return "-"; }
        const displayUrl = params.value.length > 30
          ? `${params.value.substring(0, 30)}...`
          : params.value;
        return (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {displayUrl}
          </a>
        );
      }
    },
    {
      field: "media",
      headerName: "Media",
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const mediaCount = params.value?.length || 0;
        return mediaCount > 0
          ? (
            <Chip label={`${mediaCount} item${mediaCount !== 1 ? "s" : ""}`} size="small" />
          )
          : "-";
      }
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 120,
      filterable: false,
      sortable: true,
      renderCell: (params) => {
        return new Date(params.value).toLocaleDateString();
      }
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onRowClick({ row: params.row })}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteProduct(params.id)}
          color="error"
        />
      ]
    }
  ];

  if (error) {
    return <Box>Error loading products: {error.message}</Box>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          alignItems: "center",
          flexWrap: "wrap"
        }}
      >
        <TextField
          label="Search by name"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 250 }}
        />

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Brand</InputLabel>
          <Select
            value={brandId}
            label="Brand"
            onChange={(e) => {
              setBrandId(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All Brands</MenuItem>
            {brandsData?.brands
              ?.slice()
              .sort((a, b) => getBrandName(a).toLowerCase().localeCompare(getBrandName(b).toLowerCase()))
              .map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {getBrandName(brand)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={productsData?.products || []}
          columns={columns}
          pagination
          paginationMode="server"
          sortingMode="server"
          loading={isLoading}
          rowCount={productsData?.total || 0}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(model) => {
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onRowClick={onRowClick}
          disableRowSelectionOnClick
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: CircularProgress
          }}
          slotProps={{
            toolbar: {
              onCreateNew
            }
          }}
          sx={{
            "& .MuiDataGrid-row:hover": {
              cursor: "pointer"
            }
          }}
        />
      </Box>
    </Box>
  );
};
